import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import { BusinessProvider } from './components/BusinessContext';
import { Sidebar } from './components/Sidebar';
import { MobileHeader } from './components/MobileHeader';
import { TopHeader } from './components/TopHeader';
import { Discover } from './pages/Discover';
import { Shop } from './pages/Shop';
import { Help } from './pages/Help';
import { ListingDetail } from './pages/ListingDetail';
import { Requests } from './pages/Requests';
import { Messages } from './pages/Messages';
import { Saved } from './pages/Saved';
import { MyAI } from './pages/MyAI';
import { Profile } from './pages/Profile';
import { SellFlow } from './pages/SellFlow';
import { BusinessHub } from './pages/BusinessHub';
import { Presentation } from './pages/Presentation';
import { BusinessHubWelcomeModal } from './components/BusinessHubWelcomeModal';
// Gates a route behind authentication. While signed out, it redirects to
// Discover and opens the login prompt so only Discover is usable.
function ProtectedRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated,
    openLogin
  } = useAuth();
  useEffect(() => {
    if (!isAuthenticated) openLogin();
  }, [isAuthenticated, openLogin]);
  if (!isAuthenticated) return <Navigate to="/app" replace data-id="element-0" data-mp="2zl3dbpgpde7f" />;
  return <>{children}</>;
}
function AppShell() {
  const location = useLocation();
  // The storefront is a public page — render it without any app chrome
  // (no sidebar, top header, region selector, profile, or mobile nav).
  const isPublicShop = location.pathname.startsWith('/shop/');
  if (isPublicShop) {
    return <main className="app-min-fill flex flex-col min-h-screen bg-white" data-id="element-1" data-mp="3dmfmni1cyhkc">
        <Routes data-id="element-2" data-mp="nuhhyhj2w9tr">
          <Route path="/shop/:slug" element={<Shop data-id="element-4" data-mp="nxx05t81tyze" />} data-id="element-3" data-mp="nv0xjrl9am4m" />
        </Routes>
      </main>;
  }
  // The presentation is the landing experience and a standalone full-bleed
  // deck — no app chrome at all. The product itself lives at /app.
  if (location.pathname === '/' || location.pathname.startsWith('/deck')) {
    return <Routes data-id="element-5" data-mp="3uf5hrmsxaf8s">
        <Route path="/" element={<Presentation data-id="element-7" data-mp="d4xr1jsx4jfh" />} data-id="element-6" data-mp="d4e5zu7z1ex8" />
        <Route path="/deck" element={<Presentation data-id="element-9" data-mp="d2bjhk6123x9" />} data-id="element-8" data-mp="d45tw4joadn3" />
      </Routes>;
  }
  return <div className="app-min-fill flex min-h-screen bg-white" data-id="element-10" data-mp="86w4uq0743zs">
      <Sidebar data-id="element-11" data-mp="3gl4q1d0e1shx" />

      <div className="flex-1 min-w-0 flex flex-col relative overflow-x-hidden" data-id="element-12" data-mp="3gkl6jrv3aw1s">
        <MobileHeader data-id="element-13" data-mp="3bmtixa1h9w3e" />
        <TopHeader data-id="element-14" data-mp="3bncodr9xys0j" />

        <main className="app-min-fill flex flex-col flex-1 overflow-x-hidden [&>*]:flex-1 [&>*]:min-h-0" data-id="element-15" data-mp="3bnurn7v0u6hy">
          <Routes data-id="element-16" data-mp="3qcin7e4vag7w">
            <Route path="/app" element={<Discover data-id="element-18" data-mp="3ljxu6etsc75f" />} data-id="element-17" data-mp="3qd0ud5t2ieul" />
            <Route path="/listing/:id" element={<ProtectedRoute data-id="element-20" data-mp="2xu3ii0l7comr">
                  <ListingDetail data-id="element-21" data-mp="2xt27tx83m45k" />
                </ProtectedRoute>} data-id="element-19" data-mp="3ljpj8sh83bwv" />
            <Route path="/requests" element={<ProtectedRoute data-id="element-23" data-mp="18cq6rn0wyktz">
                  <Requests data-id="element-24" data-mp="18cgchn8u5rih" />
                </ProtectedRoute>} data-id="element-22" data-mp="2svafx37gei1d" />
            <Route path="/saved" element={<ProtectedRoute data-id="element-26" data-mp="ygmv9qk699ud">
                  <Saved data-id="element-27" data-mp="yhenqsih2rgf" />
                </ProtectedRoute>} data-id="element-25" data-mp="1d62ln5fsjd9j" />
            <Route path="/messages" element={<ProtectedRoute data-id="element-29" data-mp="1r5akfx81kjd7">
                  <Messages data-id="element-30" data-mp="1r52aad3azs5p" />
                </ProtectedRoute>} data-id="element-28" data-mp="13f7tjkfv5jg6" />
            <Route path="/my-ai" element={<ProtectedRoute data-id="element-32" data-mp="1i2j71rvlvdix">
                  <MyAI data-id="element-33" data-mp="1i3azittwov4z" />
                </ProtectedRoute>} data-id="element-31" data-mp="1w2u113s7p88b" />
            <Route path="/business" element={<ProtectedRoute data-id="element-35" data-mp="6ncmyfzirqz0">
                  <BusinessHub data-id="element-36" data-mp="6n4agp7b6noc" />
                </ProtectedRoute>} data-id="element-34" data-mp="1m3l90ggwq3ol" />
            <Route path="/profile" element={<ProtectedRoute data-id="element-38" data-mp="qy71wwt8at6g">
                  <Profile data-id="element-39" data-mp="qz8fp91mptrv" />
                </ProtectedRoute>} data-id="element-37" data-mp="arc1it18ofpq" />
            <Route path="/sell" element={<ProtectedRoute data-id="element-41" data-mp="h8drhz4tok3s">
                  <SellFlow data-id="element-42" data-mp="h59hgl4oeu57" />
                </ProtectedRoute>} data-id="element-40" data-mp="m547ynkjhhpq" />
            <Route path="/help" element={<ProtectedRoute data-id="element-44" data-mp="19uyll7conab1">
                  <Help data-id="element-45" data-mp="19v88un6cf3d1" />
                </ProtectedRoute>} data-id="element-43" data-mp="ca2jlty38jo7" />
          </Routes>
        </main>
      </div>
    </div>;
}
export function App() {
  // Everyone can browse + chat freely. Registration is only required for
  // gated actions (e.g. viewing full listing details) via AuthProvider.
  // Business Hub activates adaptively via BusinessProvider.
  return <AuthProvider data-id="element-46" data-mp="3kpm86fntddwx">
      <BusinessProvider data-id="element-47" data-mp="3klye9fegipho">
        <BrowserRouter data-id="element-48" data-mp="3gmpjc77oklkj">
          <AppShell data-id="element-49" data-mp="3glehicxspnw2" />
          <BusinessHubWelcomeModal data-id="element-50" data-mp="3gkwdgu4w65dl" />
        </BrowserRouter>
      </BusinessProvider>
    </AuthProvider>;
}